import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash, Copy, PencilSimple, Check, Info } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { toast } from 'sonner';

export function AddCustomerDialog({ open, onOpenChange, initialName = '', initialPhone = '', onCreated }) {
  const [form, setForm] = useState({ name: initialName, phone: initialPhone, address: '', notes: '', group_id: '' });
  const [credentials, setCredentials] = useState([]); // [{site, user_id}]
  const [credForm, setCredForm] = useState({ site: '', user_id: '' });
  const [editIdx, setEditIdx] = useState(-1);
  const [confirmDup, setConfirmDup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);

  // Re-seed form every time the dialog flips to OPEN — Radix's `onOpenChange`
  // only fires on user-initiated close, NOT when the parent controls `open`
  // declaratively, so we use a dedicated effect. This is what wires
  // "type a number → click + Add New Customer → number lands in Mobile".
  useEffect(() => {
    if (open) {
      setForm({ name: initialName, phone: initialPhone, address: '', notes: '', group_id: '' });
      setCredentials([]);
      setCredForm({ site: '', user_id: '' });
      setEditIdx(-1);
      setShowNewGroup(false);
      setNewGroupName('');
      api.get('/customer-groups').then(r => setGroups(r.data || [])).catch(() => {});
    }
  }, [open, initialName, initialPhone]);

  const openChange = (v) => onOpenChange(v);

  const addOrUpdateCred = () => {
    const site = (credForm.site || '').trim();
    const uid = (credForm.user_id || '').trim();
    if (!site || !uid) { toast.error('Site name and user id required'); return; }
    if (editIdx >= 0) {
      setCredentials(cs => cs.map((c, i) => i === editIdx ? { site, user_id: uid } : c));
    } else {
      setCredentials(cs => [...cs, { site, user_id: uid }]);
    }
    setCredForm({ site: '', user_id: '' });
    setEditIdx(-1);
  };

  const editCred = (i) => {
    setCredForm({ site: credentials[i].site, user_id: credentials[i].user_id });
    setEditIdx(i);
  };

  const removeCred = (i) => {
    setCredentials(cs => cs.filter((_, k) => k !== i));
    if (editIdx === i) { setCredForm({ site: '', user_id: '' }); setEditIdx(-1); }
  };

  const copyToClipboard = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const submit = async (force = false) => {
    if (!form.phone || !form.name) { toast.error('Mobile and name required'); return; }
    try {
      const payload = { ...form, credentials };
      const { data } = await api.post(`/customers${force ? '?force=true' : ''}`, payload);
      toast.success('Customer added');
      onOpenChange(false);
      setConfirmDup(null);
      onCreated?.(data);
    } catch (err) {
      if (err.response?.status === 409) {
        setConfirmDup(err.response.data.detail || 'Duplicate customer detected. Save anyway?');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to add customer');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={openChange}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl">
          <DialogHeader><DialogTitle>Add New Customer</DialogTitle><DialogDescription>Quick add a customer for this invoice</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Mobile *</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-lg mt-1" autoFocus data-testid="quick-customer-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" data-testid="quick-customer-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="rounded-lg mt-1" data-testid="quick-customer-address" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Group (optional)</Label>
                {!showNewGroup && (
                  <button type="button" onClick={() => setShowNewGroup(true)} className="text-[10px] text-[#3B82F6] hover:text-[#2563EB] font-semibold" data-testid="quick-customer-add-group-btn">+ New Group</button>
                )}
              </div>
              {showNewGroup ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    placeholder="New group name..."
                    className="rounded-lg h-9 text-sm"
                    data-testid="quick-customer-new-group-input"
                    autoFocus
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={async () => {
                      const n = newGroupName.trim();
                      if (!n) { toast.error('Group name required'); return; }
                      try {
                        const { data } = await api.post('/customer-groups', { name: n });
                        setGroups(gs => [...gs, data]);
                        setForm(f => ({ ...f, group_id: data.id }));
                        setShowNewGroup(false);
                        setNewGroupName('');
                        toast.success(`Group "${data.name}" created`);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add group'); }
                    }}
                    className="h-9 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB]"
                    data-testid="quick-customer-save-group-btn"
                  >
                    <Check size={14} />
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => { setShowNewGroup(false); setNewGroupName(''); }} className="h-9 px-3 text-xs">Cancel</Button>
                </div>
              ) : (
                <Select value={form.group_id} onValueChange={v => setForm({ ...form, group_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="quick-customer-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ungrouped</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anything to remember about this customer..." className="rounded-lg mt-1 resize-none" data-testid="quick-customer-notes" />
            </div>

            {/* Saved Login IDs — sub-table for site + user-id pairs (no
                passwords). Each row exposes Copy + Edit + Delete actions. */}
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3" data-testid="customer-credentials-section">
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B] mb-2">Saved Login IDs (optional)</p>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Site Name</Label>
                  <Input value={credForm.site} onChange={e => setCredForm({ ...credForm, site: e.target.value })} placeholder="e.g. Aadhar Portal" className="rounded-lg mt-1 h-9" data-testid="cred-site-input" />
                </div>
                <div className="flex-1">
                  <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">User ID</Label>
                  <Input value={credForm.user_id} onChange={e => setCredForm({ ...credForm, user_id: e.target.value })} placeholder="e.g. 1234-5678-9012" className="rounded-lg mt-1 h-9" data-testid="cred-user-id-input" />
                </div>
                <Button type="button" onClick={addOrUpdateCred}
                  className="rounded-lg h-9 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white"
                  data-testid="cred-add-btn">
                  {editIdx >= 0 ? <><Check size={14} className="mr-1" /> Update</> : <><Plus size={14} className="mr-1" /> Add</>}
                </Button>
              </div>
              {credentials.length > 0 && (
                <div className="mt-3 border-t border-[#E4E4E7] pt-2 space-y-1">
                  {credentials.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs px-2 py-1 rounded hover:bg-white" data-testid={`cred-row-${i}`}>
                      <span className="flex-1 font-semibold truncate">{c.site}</span>
                      <span className="font-mono text-[#0A0A0A] truncate">{c.user_id}</span>
                      <button type="button" onClick={() => copyToClipboard(c.user_id)} title="Copy user id"
                        className="text-[#52525B] hover:text-[#3B82F6] p-1" data-testid={`cred-copy-${i}`}>
                        <Copy size={13} />
                      </button>
                      <button type="button" onClick={() => editCred(i)} title="Edit"
                        className="text-[#52525B] hover:text-[#3B82F6] p-1" data-testid={`cred-edit-${i}`}>
                        <PencilSimple size={13} />
                      </button>
                      <button type="button" onClick={() => removeCred(i)} title="Delete"
                        className="text-[#EF4444] hover:text-[#DC2626] p-1" data-testid={`cred-delete-${i}`}>
                        <Trash size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button onClick={() => submit(false)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="quick-save-customer">Save Customer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDup} onOpenChange={(v) => { if (!v) setConfirmDup(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Customer</AlertDialogTitle>
            <AlertDialogDescription>{confirmDup}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => submit(true)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg" data-testid="confirm-dup-customer">Save Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function AddServiceDialog({ open, onOpenChange, initialName = '', finAccounts = [], onCreated }) {
  // Mirrors the Services Master "Add/Edit Service" dialog (ServicesPage.js)
  // so the shortcut popup from /invoices/new and /invoices/:id/edit exposes
  // the SAME complete form — Group, direct Link 1 / Link 2 inputs, and the
  // Recurring subscription block — instead of a stripped-down version.
  const blankForm = {
    name: '',
    service_charge: '',
    govt_fee: '',
    default_fee_mode: '',
    requires_followup: false,
    is_recurring: false,
    recurring_interval: '',
    recurring_interval_months: '',
    link1: '',
    link2: '',
    group_id: '',
  };
  const [form, setForm] = useState({ ...blankForm, name: initialName });
  const [groups, setGroups] = useState([]);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', link1: '', link2: '' });

  // Reset form + refetch the service-groups catalog whenever the dialog is
  // opened. Driven by an effect (not the Radix `onOpenChange` hook) because
  // the parent toggles `open` directly via state — Radix doesn't fire
  // `onOpenChange(true)` in that path, which previously left the Group
  // dropdown empty on the Create/Edit invoice flow even though Masters →
  // Services worked (its page-level fetch was always live).
  useEffect(() => {
    if (!open) return;
    setForm({ ...blankForm, name: initialName });
    setShowNewGroup(false);
    setNewGroup({ name: '', link1: '', link2: '' });
    api.get('/service-groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, [open, initialName]);

  const submit = async () => {
    if (!form.name || !form.service_charge) { toast.error('Name and charge required'); return; }
    if (form.is_recurring && !form.recurring_interval) { toast.error('Pick a recurring interval'); return; }
    if (form.is_recurring && form.recurring_interval === 'custom' && (!form.recurring_interval_months || parseInt(form.recurring_interval_months) <= 0)) {
      toast.error('Enter custom interval in months');
      return;
    }
    const payload = {
      ...form,
      service_charge: parseFloat(form.service_charge),
      govt_fee: parseFloat(form.govt_fee) || 0,
      recurring_interval_months: form.is_recurring && form.recurring_interval === 'custom'
        ? parseInt(form.recurring_interval_months)
        : null,
      recurring_interval: form.is_recurring ? form.recurring_interval : '',
      link1: (form.link1 || '').trim(),
      link2: (form.link2 || '').trim(),
    };
    try {
      const { data } = await api.post('/services', payload);
      toast.success('Service added');
      onOpenChange(false);
      onCreated?.(data);
    } catch { toast.error('Failed to add service'); }
  };

  return (
    <TooltipProvider>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add New Service</DialogTitle><DialogDescription>Quick add a service to your catalog</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" data-testid="quick-service-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Service Charge *</Label>
              <Input type="number" value={form.service_charge} onChange={e => setForm({ ...form, service_charge: e.target.value })} className="rounded-lg mt-1" data-testid="quick-service-charge" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Govt Fee</Label>
              <Input type="number" value={form.govt_fee} onChange={e => setForm({ ...form, govt_fee: e.target.value })} className="rounded-lg mt-1" data-testid="quick-service-govtfee" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Group (optional)</Label>
              {!showNewGroup && (
                <button type="button" onClick={() => setShowNewGroup(true)} className="text-[10px] text-[#3B82F6] hover:text-[#2563EB] font-semibold" data-testid="quick-service-add-group-btn">+ New Group</button>
              )}
            </div>
            {showNewGroup ? (
              <div className="space-y-2 mt-1 p-2 rounded-lg bg-[#EFF6FF] border border-[#DBEAFE]">
                <Input value={newGroup.name} onChange={e => setNewGroup(g => ({ ...g, name: e.target.value }))} placeholder="Group name (e.g. eDistrict)" className="rounded-lg h-9 text-sm" autoFocus data-testid="quick-service-new-group-name" />
                <Input value={newGroup.link1} onChange={e => setNewGroup(g => ({ ...g, link1: e.target.value }))} placeholder="Link 1 (Invoice shortcut URL)" className="rounded-lg h-9 text-sm" data-testid="quick-service-new-group-link1" />
                <Input value={newGroup.link2} onChange={e => setNewGroup(g => ({ ...g, link2: e.target.value }))} placeholder="Link 2 (Follow-up shortcut URL)" className="rounded-lg h-9 text-sm" data-testid="quick-service-new-group-link2" />
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" className="h-8 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB]" onClick={async () => {
                    const n = newGroup.name.trim();
                    if (!n) { toast.error('Group name required'); return; }
                    try {
                      const { data } = await api.post('/service-groups', { name: n, link1: newGroup.link1.trim(), link2: newGroup.link2.trim() });
                      setGroups(gs => [...gs, data]);
                      // Auto-fill links on current service from the group's defaults.
                      setForm(f => ({ ...f, group_id: data.id, link1: data.link1, link2: data.link2 }));
                      setShowNewGroup(false);
                      setNewGroup({ name: '', link1: '', link2: '' });
                      toast.success(`Group "${data.name}" created`);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add group'); }
                  }} data-testid="quick-service-save-group">Save Group</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => { setShowNewGroup(false); setNewGroup({ name: '', link1: '', link2: '' }); }} className="h-8 px-3 text-xs">Cancel</Button>
                </div>
              </div>
            ) : (
              <Select value={form.group_id || '__none__'} onValueChange={v => {
                if (v === '__none__') { setForm(f => ({ ...f, group_id: '' })); return; }
                const grp = groups.find(g => g.id === v);
                // On group pick, auto-fill the link inputs (user can still tweak before saving).
                setForm(f => ({ ...f, group_id: v, link1: grp?.link1 || f.link1, link2: grp?.link2 || f.link2 }));
              }}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="quick-service-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Ungrouped</SelectItem>
                  {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Default Gov Fee Mode</Label>
            <Select value={form.default_fee_mode} onValueChange={v => setForm({ ...form, default_fee_mode: v })}>
              <SelectTrigger className="rounded-lg mt-1" data-testid="quick-service-fee-mode"><SelectValue placeholder="Select account..." /></SelectTrigger>
              <SelectContent>{finAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="quick-followup" checked={form.requires_followup} onChange={e => setForm({ ...form, requires_followup: e.target.checked })} className="rounded" data-testid="quick-service-followup" />
            <Label htmlFor="quick-followup" className="text-sm text-[#52525B] cursor-pointer">Requires Follow-up / Processing</Label>
          </div>

          {/* Direct Link 1 / Link 2 inputs — auto-filled when a Group is
              picked; user can tweak before saving. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B] inline-flex items-center gap-1 cursor-help">
                    Link 1 <Info size={11} className="text-[#A1A1AA]" />
                  </Label>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-[#0A0A0A] text-white border-0 text-xs">For Invoice Page link</TooltipContent>
              </Tooltip>
              <Input value={form.link1} onChange={e => setForm({ ...form, link1: e.target.value })}
                placeholder="https://…" className="rounded-lg mt-1" data-testid="quick-service-link1" />
            </div>
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B] inline-flex items-center gap-1 cursor-help">
                    Link 2 <Info size={11} className="text-[#A1A1AA]" />
                  </Label>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-[#0A0A0A] text-white border-0 text-xs">For Follow-up Page link</TooltipContent>
              </Tooltip>
              <Input value={form.link2} onChange={e => setForm({ ...form, link2: e.target.value })}
                placeholder="https://…" className="rounded-lg mt-1" data-testid="quick-service-link2" />
            </div>
          </div>

          {/* Recurring subscription — when enabled, every invoice that
              contains this service shows up on the Recurring page once the
              renewal date approaches. Mirrors ServicesPage.js. */}
          <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="quick-is-recurring"
                checked={!!form.is_recurring}
                onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                className="rounded h-4 w-4 accent-[#3B82F6]"
                data-testid="quick-service-recurring-checkbox"
              />
              <Label htmlFor="quick-is-recurring" className="text-sm font-semibold text-[#0A0A0A] cursor-pointer">
                Recurring (subscription-style)
              </Label>
            </div>
            {form.is_recurring && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Renewal Interval</Label>
                  <Select value={form.recurring_interval} onValueChange={v => setForm({ ...form, recurring_interval: v })}>
                    <SelectTrigger className="rounded-lg mt-1 h-9" data-testid="quick-service-recurring-interval">
                      <SelectValue placeholder="Choose interval..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly (1 month)</SelectItem>
                      <SelectItem value="quarterly">Quarterly (3 months)</SelectItem>
                      <SelectItem value="half_yearly">Half-Yearly (6 months)</SelectItem>
                      <SelectItem value="yearly">Yearly (12 months)</SelectItem>
                      <SelectItem value="custom">Custom (months)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.recurring_interval === 'custom' && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Custom Months</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.recurring_interval_months}
                      onChange={e => setForm({ ...form, recurring_interval_months: e.target.value })}
                      placeholder="e.g. 18"
                      className="rounded-lg mt-1 h-9 font-mono"
                      data-testid="quick-service-recurring-months"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="quick-save-service">Save Service</Button></DialogFooter>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
}
